import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { JobClient, isFinished, type JobDTO } from "../../api";
import { useAuth } from "../../auth";

interface JobCenterValue {
  jobs: JobDTO[];
  /** Pending + running, the number on the bell. */
  activeCount: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  client: JobClient;
  refresh: () => void;
}

const JobCenterContext = createContext<JobCenterValue | null>(null);

/**
 * The half of the job centre that never changes: starting an export needs to
 * refresh the list and open the panel, but not to know what is in it.
 *
 * Kept separate because the value above carries `jobs`, which is a new array on
 * every poll — two seconds apart while work runs. A page that only wants to
 * *start* an export would otherwise re-render at that cadence for no reason.
 */
interface JobActionsValue {
  refresh: () => void;
  setOpen: (open: boolean) => void;
  client: JobClient;
}

const JobActionsContext = createContext<JobActionsValue | null>(null);

/** Fast enough to feel live while work runs; idle polling stays cheap. */
const ACTIVE_INTERVAL = 2_000;
const IDLE_INTERVAL = 30_000;

/**
 * Tracks background jobs for the signed-in account.
 *
 * The polling rate follows the work: two seconds while something is running,
 * thirty when nothing is. The slow tick still matters — a job started in
 * another tab, or before a reload, should appear here without the user doing
 * anything.
 *
 * Completion is announced with a toast rather than only inside the panel. Jobs
 * run *while* people work, so the notification that matters most is the one
 * that arrives without opening anything.
 */
export function JobCenterProvider({ children }: { children: ReactNode }) {
  const { http, status } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const client = useMemo(() => new JobClient(http), [http]);

  const { data } = useQuery({
    queryKey: ["job", "mine"],
    queryFn: () => client.mine(),
    enabled: status === "authenticated",
    refetchInterval: (query) => {
      const jobs = query.state.data as JobDTO[] | undefined;
      return jobs?.some((job) => !isFinished(job.status)) ? ACTIVE_INTERVAL : IDLE_INTERVAL;
    },
    // A failed poll is not worth a toast; the next tick will retry.
    retry: false,
  });

  const jobs = useMemo(() => data ?? [], [data]);
  const activeCount = jobs.filter((job) => !isFinished(job.status)).length;

  // Announce transitions into a finished state exactly once. The previous
  // statuses live in a ref so this compares against the last poll rather than
  // re-announcing every render.
  const seen = useRef(new Map<string, JobDTO["status"]>());
  useEffect(() => {
    for (const job of jobs) {
      const previous = seen.current.get(job.id);
      seen.current.set(job.id, job.status);

      if (previous === undefined || previous === job.status || !isFinished(job.status)) {
        continue;
      }

      if (job.status === "SUCCEEDED") {
        toast.success(job.label, {
          description: t("job.finished", "Finished"),
          action: { label: t("job.view", "View"), onClick: () => setOpen(true) },
        });
      } else if (job.status === "FAILED") {
        toast.error(job.label, {
          description: job.error ?? t("job.failed", "Failed"),
          action: { label: t("job.view", "View"), onClick: () => setOpen(true) },
        });
      }
    }
  }, [jobs, t]);

  const refresh = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: ["job", "mine"] }),
    [queryClient],
  );

  // `setOpen` is a state setter and `client`/`refresh` are memoised, so this
  // value is created once and never invalidates its consumers.
  const actions = useMemo<JobActionsValue>(
    () => ({ refresh, setOpen, client }),
    [refresh, client],
  );

  const value = useMemo<JobCenterValue>(
    () => ({
      jobs,
      activeCount,
      open,
      setOpen,
      client,
      refresh,
    }),
    [jobs, activeCount, open, client, refresh],
  );

  return (
    <JobActionsContext.Provider value={actions}>
      <JobCenterContext.Provider value={value}>{children}</JobCenterContext.Provider>
    </JobActionsContext.Provider>
  );
}

/** The stable half — for callers that start jobs but do not display them. */
export function useJobActions(): JobActionsValue {
  const context = useContext(JobActionsContext);
  if (!context) {
    throw new Error("useJobActions must be used inside a JobCenterProvider");
  }
  return context;
}

export function useJobCenter(): JobCenterValue {
  const context = useContext(JobCenterContext);
  if (!context) {
    throw new Error("useJobCenter must be used inside a JobCenterProvider");
  }
  return context;
}
